<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import {
  CatalogueFilterType,
  CatalogueFilterOp,
  CatalogueFilterGroup,
  CATALOGUE_FILTER_OP_LABELS,
  CATALOGUE_SORT_OPTIONS,
  type CatalogueFilterValue,
  type CatalogueFilterDefinition,
  type CatalogueSortOrder,
  type CatalogueSortField,
} from '@/models/CatalogueFilters';
import type { CatalogueItemGroup } from '@/models/Catalogue';
import { useCatalogueGroupAutocomplete } from '@/composables/catalogue/useCatalogueGroupAutocomplete';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = defineProps<{
  filters: Map<string, CatalogueFilterValue>;
  activeFilterKeys: Set<string>;
  operatorOverrides: Map<string, CatalogueFilterOp>;
  filterDefinitions: CatalogueFilterDefinition[];
  groups: CatalogueItemGroup[];
  sortBy: CatalogueSortField | null;
  sortOrder: CatalogueSortOrder;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:filter', key: string, value: CatalogueFilterValue): void;
  (e: 'update:operator', key: string, op: CatalogueFilterOp): void;
  (e: 'add:filter', key: string): void;
  (e: 'remove:filter', key: string): void;
  (e: 'swap:filter', oldKey: string, newKey: string): void;
  (e: 'clear:filters'): void;
  (
    e: 'update:sorting',
    sortBy: CatalogueSortField | null,
    sortOrder: CatalogueSortOrder,
  ): void;
}>();

// Group autocomplete
const groupsRef = computed(() => props.groups);
const { autocompleteOptions } = useCatalogueGroupAutocomplete(groupsRef);

// Pending slot: an empty chip waiting for the user to pick a field
const pendingSlot = ref(false);
const pendingFieldMenuOpen = ref(false);

// Search input for filter selection menus
const filterSearchQuery = ref('');

const setFilterSearchRef = (el: unknown) => {
  if (el instanceof HTMLInputElement) {
    // Delay focus slightly so the menu transition has made the element visible
    setTimeout(() => el.focus(), 50);
  }
};

const availableFilters = computed(() => {
  return props.filterDefinitions.filter(
    (f) => !props.activeFilterKeys.has(f.key),
  );
});

/**
 * Available fields for a field-change dropdown on an existing chip.
 * Includes the chip's own definition so it appears as "active" in the list.
 */
const getSwappableFields = (currentKey: string) => {
  return props.filterDefinitions.filter(
    (f) => f.key === currentKey || !props.activeFilterKeys.has(f.key),
  );
};

/**
 * Returns a flat list of filter definitions sorted with General first,
 * filtered by the current search query.
 */
const getSearchableFilters = (defs: CatalogueFilterDefinition[]) => {
  const query = filterSearchQuery.value.toLowerCase().trim();
  const filtered = query
    ? defs.filter(
        (f) =>
          f.label.toLowerCase().includes(query) ||
          (f.group?.toLowerCase().includes(query) ?? false),
      )
    : defs;
  return [
    ...filtered.filter((f) => f.group === CatalogueFilterGroup.GENERAL),
    ...filtered.filter((f) => f.group !== CatalogueFilterGroup.GENERAL),
  ];
};

const searchableAvailableFilters = computed(() =>
  getSearchableFilters(availableFilters.value),
);

const searchableSwappableFieldsMap = computed(() => {
  const map = new Map<string, CatalogueFilterDefinition[]>();
  props.activeFilterKeys.forEach((key) => {
    map.set(key, getSearchableFilters(getSwappableFields(key)));
  });
  return map;
});

const activeFilters = computed(() => {
  const result: CatalogueFilterDefinition[] = [];
  props.activeFilterKeys.forEach((key) => {
    const def = props.filterDefinitions.find((f) => f.key === key);
    if (def) {
      result.push(def);
    }
  });
  return result;
});

const hasActiveFilters = computed(() => props.activeFilterKeys.size > 0);

const handleClearFilters = () => {
  pendingSlot.value = false;
  emit('clear:filters');
};

const getFilterValue = (key: string): CatalogueFilterValue => {
  return props.filters.get(key) ?? null;
};

const getEffectiveOp = (
  filter: CatalogueFilterDefinition,
): CatalogueFilterOp => {
  return props.operatorOverrides.get(filter.key) ?? filter.defaultOp;
};

const getOpLabel = (op: CatalogueFilterOp): string => {
  return CATALOGUE_FILTER_OP_LABELS[op];
};

const opTooltips: Record<CatalogueFilterOp, string> = {
  [CatalogueFilterOp.EQ]: $gettext('Equals'),
  [CatalogueFilterOp.NEQ]: $gettext('Not equals'),
  [CatalogueFilterOp.CONTAINS]: $gettext('Contains'),
  [CatalogueFilterOp.STARTS_WITH]: $gettext('Starts with'),
  [CatalogueFilterOp.IN]: $gettext('In list'),
  [CatalogueFilterOp.BETWEEN]: $gettext('Between'),
  [CatalogueFilterOp.GT]: $gettext('Greater than'),
  [CatalogueFilterOp.GTE]: $gettext('Greater than or equal'),
  [CatalogueFilterOp.LT]: $gettext('Less than'),
  [CatalogueFilterOp.LTE]: $gettext('Less than or equal'),
  [CatalogueFilterOp.DESCENDANTS]: $gettext('Includes descendants'),
};

const getOpTooltip = (op: CatalogueFilterOp): string => {
  return opTooltips[op];
};

const handleFilterChange = (key: string, value: CatalogueFilterValue) => {
  emit('update:filter', key, value);
};

const handleOperatorChange = (key: string, op: CatalogueFilterOp) => {
  emit('update:operator', key, op);
};

const handleRemoveFilter = (key: string) => {
  emit('remove:filter', key);
};

// --- Pending slot ---

const addPendingSlot = () => {
  if (pendingSlot.value) {
    // Already have a pending slot — just reopen its menu
    pendingFieldMenuOpen.value = true;
    return;
  }
  pendingSlot.value = true;
  nextTick(() => {
    pendingFieldMenuOpen.value = true;
  });
};

const cancelPendingSlot = () => {
  pendingSlot.value = false;
  pendingFieldMenuOpen.value = false;
  filterSearchQuery.value = '';
};

const commitPendingSlot = (key: string) => {
  pendingSlot.value = false;
  pendingFieldMenuOpen.value = false;
  filterSearchQuery.value = '';
  emit('add:filter', key);
  nextTick(() => focusFilterInput(key));
};

// --- Field swap on existing chip ---

const handleFieldSwap = (oldKey: string, newKey: string) => {
  filterSearchQuery.value = '';
  if (oldKey === newKey) {
    return;
  }
  delete localInputValues.value[oldKey];
  emit('swap:filter', oldKey, newKey);
  nextTick(() => focusFilterInput(newKey));
};

// Local state for text/number inputs to avoid value reset during debounce
const localInputValues = ref<Record<string, string>>({});

const getLocalInputValue = (key: string): string => {
  if (key in localInputValues.value) {
    return localInputValues.value[key];
  }
  const val = getFilterValue(key);
  return val !== null && val !== undefined ? String(val) : '';
};

// Sync local values when props change externally (e.g., clear all)
watch(
  () => props.filters,
  () => {
    for (const key of Object.keys(localInputValues.value)) {
      const propValue = props.filters.get(key);
      localInputValues.value[key] =
        propValue !== null && propValue !== undefined ? String(propValue) : '';
    }
  },
  { deep: true },
);

// Debounce for text/number inputs
const debounceTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

const handleTextInput = (key: string, value: string) => {
  localInputValues.value[key] = value;

  if (debounceTimeouts[key]) {
    clearTimeout(debounceTimeouts[key]);
  }
  debounceTimeouts[key] = setTimeout(() => {
    handleFilterChange(key, value || null);
  }, 300);
};

const handleNumberInput = (key: string, value: string) => {
  localInputValues.value[key] = value;

  if (debounceTimeouts[key]) {
    clearTimeout(debounceTimeouts[key]);
  }
  debounceTimeouts[key] = setTimeout(() => {
    if (value === '') {
      handleFilterChange(key, null);
    } else {
      const num = Number(value);
      handleFilterChange(key, isNaN(num) ? null : num);
    }
  }, 300);
};

const handleDateInput = (key: string, value: string) => {
  handleFilterChange(key, value || null);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterInputRefs = ref<Record<string, any>>({});

// Focus or open the input for a given filter key
const focusFilterInput = (key: string) => {
  const filter = props.filterDefinitions.find((f) => f.key === key);
  const inputRef = filterInputRefs.value[key];
  if (!filter || !inputRef) {
    return;
  }

  if (
    filter.type === CatalogueFilterType.TEXT ||
    filter.type === CatalogueFilterType.NUMBER ||
    filter.type === CatalogueFilterType.DATE
  ) {
    inputRef.focus();
  } else if (filter.type === CatalogueFilterType.BOOLEAN) {
    return;
  } else {
    inputRef.focus();
    inputRef.menu = true;
  }
};

// Sorting
const sortOptions = CATALOGUE_SORT_OPTIONS;

const sortLabel = computed(() => {
  if (!props.sortBy) {
    return null;
  }
  return sortOptions.find((o) => o.value === props.sortBy)?.label ?? null;
});

const handleSortFieldChange = (value: CatalogueSortField | null) => {
  emit('update:sorting', value, props.sortOrder);
};

const toggleSortOrder = () => {
  const newOrder = props.sortOrder === 'asc' ? 'desc' : 'asc';
  emit('update:sorting', props.sortBy, newOrder);
};
</script>

<template>
  <div class="filter-bar-container">
    <v-icon icon="mdi-filter-variant" size="small" class="bar-icon" />

    <!-- Active filters as chips -->
    <div v-for="filter in activeFilters" :key="filter.key" class="filter-chip">
      <!-- Field segment -->
      <v-menu
        :close-on-content-click="true"
        @update:model-value="!$event && (filterSearchQuery = '')"
      >
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="chip-segment chip-segment-field"
            @click.stop
          >
            <span class="filter-label">{{ filter.label }}</span>
          </span>
        </template>
        <v-card class="field-select-dropdown" elevation="8" rounded="sm">
          <div class="filter-search-sticky">
            <input
              :ref="setFilterSearchRef"
              v-model="filterSearchQuery"
              type="text"
              :placeholder="$gettext('Search filters...')"
              class="filter-search-input"
              @click.stop
              @keydown.stop
            />
          </div>
          <v-list density="compact" class="field-select-list">
            <v-list-item
              v-for="f in searchableSwappableFieldsMap.get(filter.key) ?? []"
              :key="f.key"
              :active="f.key === filter.key"
              @click="handleFieldSwap(filter.key, f.key)"
            >
              <template #prepend>
                <v-icon :icon="f.icon" size="small" />
              </template>
              <v-list-item-title>{{ f.label }}</v-list-item-title>
              <template #append>
                <span class="filter-group-hint">{{ f.group }}</span>
              </template>
            </v-list-item>
            <v-list-item
              v-if="
                (searchableSwappableFieldsMap.get(filter.key) ?? []).length ===
                0
              "
              disabled
            >
              <v-list-item-title class="no-results-text">
                {{ $gettext('No matching filters') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Operator segment -->
      <v-menu
        v-if="filter.availableOps.length > 1"
        :close-on-content-click="true"
      >
        <template #activator="{ props: menuProps }">
          <v-tooltip
            :text="getOpTooltip(getEffectiveOp(filter))"
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="{ ...menuProps, ...tooltipProps }"
                class="chip-segment chip-segment-op"
                @click.stop
              >
                <span class="filter-op">
                  {{ getOpLabel(getEffectiveOp(filter)) }}
                </span>
              </span>
            </template>
          </v-tooltip>
        </template>
        <v-list density="compact">
          <v-list-item
            v-for="op in filter.availableOps"
            :key="op"
            :active="getEffectiveOp(filter) === op"
            @click="handleOperatorChange(filter.key, op)"
          >
            <template #prepend>
              <span class="op-list-symbol">{{ getOpLabel(op) }}</span>
            </template>
            <v-list-item-title>{{ getOpTooltip(op) }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Value segment -->
      <span class="chip-segment chip-segment-value">
        <!-- Text filter -->
        <input
          v-if="filter.type === CatalogueFilterType.TEXT"
          :ref="
            (el) => {
              if (el) filterInputRefs[filter.key] = el as HTMLInputElement;
            }
          "
          type="text"
          :value="getLocalInputValue(filter.key)"
          :placeholder="filter.placeholder"
          class="chip-text-input"
          @input="
            handleTextInput(
              filter.key,
              ($event.target as HTMLInputElement).value,
            )
          "
          @click.stop
          @keydown.stop
        />

        <!-- Number filter -->
        <input
          v-else-if="filter.type === CatalogueFilterType.NUMBER"
          :ref="
            (el) => {
              if (el) filterInputRefs[filter.key] = el as HTMLInputElement;
            }
          "
          type="number"
          :value="getLocalInputValue(filter.key)"
          :placeholder="filter.placeholder"
          class="chip-text-input chip-number-input"
          @input="
            handleNumberInput(
              filter.key,
              ($event.target as HTMLInputElement).value,
            )
          "
          @click.stop
          @keydown.stop
        />

        <!-- Date filter -->
        <input
          v-else-if="filter.type === CatalogueFilterType.DATE"
          :ref="
            (el) => {
              if (el) filterInputRefs[filter.key] = el as HTMLInputElement;
            }
          "
          type="date"
          :value="getLocalInputValue(filter.key)"
          class="chip-text-input chip-date-input"
          @change="
            handleDateInput(
              filter.key,
              ($event.target as HTMLInputElement).value,
            )
          "
          @click.stop
          @keydown.stop
        />

        <!-- Select filter -->
        <v-select
          v-else-if="filter.type === CatalogueFilterType.SELECT"
          :ref="
            (el) => {
              if (el) filterInputRefs[filter.key] = el;
            }
          "
          :model-value="getFilterValue(filter.key) as string"
          :items="filter.options"
          density="compact"
          variant="plain"
          hide-details
          class="chip-input"
          :menu-props="{ offset: 10 }"
          @update:model-value="handleFilterChange(filter.key, $event)"
        />

        <!-- Autocomplete filter -->
        <v-autocomplete
          v-else-if="filter.type === CatalogueFilterType.AUTOCOMPLETE"
          :ref="
            (el) => {
              if (el) filterInputRefs[filter.key] = el;
            }
          "
          :model-value="getFilterValue(filter.key)"
          :items="autocompleteOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="plain"
          hide-details
          class="chip-input"
          :menu-props="{ offset: 10 }"
          @update:model-value="handleFilterChange(filter.key, $event)"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps">
              <v-list-item-subtitle>{{ item.raw.path }}</v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-autocomplete>

        <!-- Boolean filter -->
        <v-switch
          v-else-if="filter.type === CatalogueFilterType.BOOLEAN"
          :ref="
            (el) => {
              if (el) filterInputRefs[filter.key] = el;
            }
          "
          :model-value="getFilterValue(filter.key) === true"
          color="primary"
          density="compact"
          hide-details
          class="chip-switch"
          @update:model-value="handleFilterChange(filter.key, $event)"
        />
      </span>

      <!-- Remove button -->
      <v-icon
        size="x-small"
        class="chip-close"
        @click="handleRemoveFilter(filter.key)"
      >
        mdi-close
      </v-icon>
    </div>

    <!-- Pending empty chip — waiting for field selection -->
    <div v-if="pendingSlot" class="filter-chip filter-chip-pending">
      <v-menu
        v-model="pendingFieldMenuOpen"
        :close-on-content-click="true"
        @update:model-value="!$event && (filterSearchQuery = '')"
      >
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="chip-segment chip-segment-field"
            @click.stop
          >
            <span class="filter-label filter-label-placeholder">
              {{ $gettext('Select field...') }}
            </span>
          </span>
        </template>
        <v-card class="field-select-dropdown" elevation="8" rounded="sm">
          <div class="filter-search-sticky">
            <input
              :ref="setFilterSearchRef"
              v-model="filterSearchQuery"
              type="text"
              :placeholder="$gettext('Search filters...')"
              class="filter-search-input"
              @click.stop
              @keydown.stop
            />
          </div>
          <v-list density="compact" class="field-select-list">
            <v-list-item
              v-for="f in searchableAvailableFilters"
              :key="f.key"
              @click="commitPendingSlot(f.key)"
            >
              <template #prepend>
                <v-icon :icon="f.icon" size="small" />
              </template>
              <v-list-item-title>{{ f.label }}</v-list-item-title>
              <template #append>
                <span class="filter-group-hint">{{ f.group }}</span>
              </template>
            </v-list-item>
            <v-list-item
              v-if="searchableAvailableFilters.length === 0"
              disabled
            >
              <v-list-item-title class="no-results-text">
                {{ $gettext('No matching filters') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
      <v-icon size="x-small" class="chip-close" @click="cancelPendingSlot">
        mdi-close
      </v-icon>
    </div>

    <!-- Add filter button -->
    <v-btn
      v-if="availableFilters.length > 0"
      variant="text"
      size="small"
      class="add-filter-btn"
      @click="addPendingSlot"
    >
      <v-icon icon="mdi-plus" size="small" class="mr-1" />
      {{ $gettext('Add filter') }}
    </v-btn>

    <!-- Clear all filters button -->
    <v-btn
      v-if="hasActiveFilters"
      variant="text"
      size="small"
      class="clear-btn"
      @click="handleClearFilters"
    >
      {{ $gettext('Clear all') }}
    </v-btn>

    <!-- Sort control (segmented, matching filter style) -->
    <div class="filter-chip ml-auto">
      <!-- Sort field segment -->
      <v-menu :close-on-content-click="true">
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="chip-segment chip-segment-field"
            @click.stop
          >
            <v-icon icon="mdi-sort" size="x-small" class="segment-icon" />
            <span v-if="sortLabel" class="filter-label">{{ sortLabel }}</span>
            <span v-else class="filter-label filter-label-placeholder">
              {{ $gettext('Sort by...') }}
            </span>
          </span>
        </template>
        <v-list density="compact" class="field-select-list">
          <v-list-item
            v-for="opt in sortOptions"
            :key="opt.value"
            :active="sortBy === opt.value"
            @click="handleSortFieldChange(opt.value)"
          >
            <v-list-item-title>{{ opt.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Sort order segment -->
      <span
        v-if="sortBy"
        class="chip-segment chip-segment-op"
        @click="toggleSortOrder"
      >
        <v-icon
          :icon="
            sortOrder === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'
          "
          size="small"
        />
      </span>

      <!-- Clear sort -->
      <v-icon
        v-if="sortBy"
        size="x-small"
        class="chip-close"
        @click="handleSortFieldChange(null)"
      >
        mdi-close
      </v-icon>
    </div>

    <!-- Loading indicator -->
    <v-progress-circular v-if="loading" indeterminate size="18" width="2" />
  </div>
</template>

<style scoped>
.filter-bar-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  min-height: 40px;
}

.bar-icon {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* --- Filter chip (segmented container) --- */

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  padding: 0 2px;
}

.filter-chip-pending {
  border-style: dashed;
}

/* --- Segments --- */

.chip-segment {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 13px;
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.chip-segment-field {
  font-weight: 500;
  cursor: pointer;
}

.chip-segment-field:hover {
  background: rgba(var(--v-theme-on-surface), 0.1);
}

.chip-segment-op {
  padding: 0 5px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
}

.chip-segment-op:hover {
  background: rgba(var(--v-theme-on-surface), 0.14);
}

.chip-segment-value {
  background: transparent;
  padding: 0 4px;
}

/* --- Close button --- */

.chip-close {
  cursor: pointer;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-left: 2px;
  margin-right: 2px;
}

.chip-close:hover {
  color: rgb(var(--v-theme-on-surface));
}

/* --- Field label --- */

.filter-label {
  font-weight: 500;
  font-size: 13px;
  white-space: nowrap;
}

.filter-label-placeholder {
  color: rgba(var(--v-theme-on-surface), 0.5);
  font-weight: 400;
}

/* --- Operator --- */

.filter-op {
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  white-space: nowrap;
}

/* --- Value inputs --- */

.chip-text-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-family: inherit;
  color: inherit;
  min-width: 80px;
  max-width: 120px;
}

.chip-number-input {
  max-width: 80px;
}

.chip-number-input::-webkit-inner-spin-button,
.chip-number-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.chip-number-input {
  -moz-appearance: textfield;
}

.chip-date-input {
  max-width: 130px;
}

.chip-text-input::placeholder {
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.chip-input {
  min-width: 80px;
  max-width: 140px;
  display: flex;
  align-items: center;
}

.chip-input :deep(.v-input__control) {
  min-height: unset;
}

.chip-input :deep(.v-field) {
  padding: 0;
  height: auto;
  align-items: center;
  display: flex;
}

.chip-input :deep(.v-field__field) {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.chip-input :deep(.v-field__input) {
  padding: 0;
  min-height: unset;
  font-size: 13px;
  line-height: normal;
  display: flex;
  align-items: center;
}

.chip-input :deep(.v-select__selection) {
  margin: 0;
  line-height: normal;
}

.chip-input :deep(.v-field__append-inner) {
  display: none;
}

.chip-input :deep(.v-field__outline) {
  display: none;
}

.chip-switch :deep(.v-switch__thumb) {
  height: 10px;
  width: 10px;
}

/* --- Buttons --- */

.add-filter-btn,
.clear-btn {
  font-size: 13px;
  text-transform: none;
  letter-spacing: normal;
}

.field-select-dropdown {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  width: max-content;
  min-width: 200px;
  overflow: hidden;
}

.filter-search-sticky {
  flex-shrink: 0;
  padding: 8px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.field-select-list {
  overflow-y: auto;
  flex: 1;
}

.filter-search-input {
  width: 100%;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  background: transparent;
  color: inherit;
}

.filter-search-input:focus {
  border-color: rgb(var(--v-theme-primary));
}

.filter-search-input::placeholder {
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.no-results-text {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.5);
  text-align: center;
}

.filter-group-hint {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.4);
  margin-left: 12px;
  white-space: nowrap;
}

/* --- Operator list symbol --- */

.op-list-symbol {
  font-weight: 700;
  font-size: 14px;
  min-width: 24px;
  text-align: center;
}

/* --- Segment icon --- */

.segment-icon {
  margin-right: 4px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
